import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import TeacherLayout from '../components/layout/TeacherLayout';
import AdminLayout from '../components/layout/AdminLayout';
import StudentLayout from '../components/layout/StudentLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Paper,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Card, CardHeader, CardContent, CardActions, List, ListItem, ListItemIcon, ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Drawer,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { 
  PhotoCamera, 
  Save, 
  ArrowBack, 
  VpnKey as VpnKeyIcon, 
  CheckCircle, 
  HighlightOff, 
  Payment,
  Person,
  CreditCard,
  School,
  ExitToApp,
  Menu as MenuIcon,
  Add,
  ShoppingCart,
  History,
  Assessment,
} from '@mui/icons-material';
import api, { authAPI } from '../services/api';
import { 
  BASE_URL,
  ADDON_CATALOG,
  ADDONS_HISTORY,
  ENTITLEMENTS,
  ADDONS_CART,
  ADDONS_CART_CLEAR,
  STUDENT_TEACHERS,
  STUDENT_FAMILY,
  SUBSCRIPTION_CHANGE,
  FEATURE_ACCESS,
  STUDENT_EXIT_TEACHER,
  SUBSCRIPTION_TOGGLE_AUTO_RENEW,
  SUBSCRIPTION_CANCEL_SCHEDULED_DOWNGRADE,
  STUDENT_PLANS,
  STUDENT_REQUEST_UPGRADE,
} from '../config/Config';
import { PRICING_PLANS, requiresPayment, getPlanDisplayConfig } from '../config/Plans';
import { validatePlanChange } from '../services/subscriptionService';
import {
  getBillingSummary,
  getUpgradePreview,
  startUpgrade,
  createCheckoutSubscription,
  createCheckoutAddon,
  createPortalSession,
  changePlan as billingChangePlan,
  cancelSubscription as billingCancelSubscription,
  reactivateSubscription as billingReactivateSubscription,
} from '../services/billingService';

/**
 * Formats a date string to YYYY-MM-DD format
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or empty string if invalid
 */
/** Maximum number of history entries shown for subscription and add-on history */
const HISTORY_MAX_ENTRIES = 10;

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const calculateNextRenewalDate = (endDate, planDuration = 'monthly') => {
  if (!endDate) return '';
  
  try {
    const end = new Date(endDate);
    const now = new Date();
    
    // For custom plans, no renewal date
    if (planDuration?.toLowerCase() === 'custom') {
      return '';
    }
    
    // For monthly plans, show one day before end date
    if (planDuration?.toLowerCase() === 'monthly') {
      const renewalDate = new Date(end);
      renewalDate.setDate(renewalDate.getDate() - 1);
      return formatDate(renewalDate);
    }
    
    // For other plans, calculate next renewal
    const baseDate = end > now ? end : now;
    
    let nextRenewal;
    switch (planDuration?.toLowerCase()) {
      case 'yearly':
        nextRenewal = new Date(baseDate);
        nextRenewal.setFullYear(nextRenewal.getFullYear() + 1);
        break;
      case 'weekly':
        nextRenewal = new Date(baseDate);
        nextRenewal.setDate(nextRenewal.getDate() + 7);
        break;
      case 'onetime':
        // For one-time purchases, show the end date as the renewal date
        return formatDate(endDate);
      default:
        // Default to monthly calculation
        nextRenewal = new Date(baseDate);
        nextRenewal.setMonth(nextRenewal.getMonth() + 1);
        nextRenewal.setDate(nextRenewal.getDate() - 1); // One day before
    }
    
    return formatDate(nextRenewal);
  } catch (error) {
    console.error('Error calculating renewal date:', error);
    return '';
  }
};

const isPlanExpired = (subscription) => {
  if (!subscription) return false;
  // Paid and active (registration flow) – do not show as expired even if end_date is in the past
  if (subscription.subscription_status === 'active' && subscription.payment_completed === true) {
    return false;
  }
  if (!subscription.end_date) return false;
  try {
    const endDate = new Date(subscription.end_date);
    const now = new Date();
    return endDate < now && (
      !subscription.is_active ||
      subscription.payment_status !== 'active'
    );
  } catch (error) {
    console.error('Error checking if plan is expired:', error);
    return false;
  }
};

const getSubscriptionPlanCode = (subscription) =>
  subscription?.pricing_plan?.code ||
  subscription?.plan_code ||
  subscription?.pricing_plan_code ||
  null;

const getSubscriptionPlanId = (subscription) =>
  typeof subscription?.pricing_plan === 'object'
    ? subscription?.pricing_plan?.id
    : subscription?.pricing_plan || subscription?.pricing_plan_id || null;

const getMostRecentSubscription = (subscriptions = [], predicate = () => true) => {
  const matches = subscriptions.filter(predicate);
  if (!matches.length) return null;
  return [...matches].sort((a, b) => {
    const aTime = a?.start_date ? new Date(a.start_date).getTime() : 0;
    const bTime = b?.start_date ? new Date(b.start_date).getTime() : 0;
    return bTime - aTime;
  })[0];
};

const getRolePlanConfigs = (roleName = 'Student') => {
  const roleKey = roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase();
  return Array.isArray(PRICING_PLANS[roleKey]) ? PRICING_PLANS[roleKey] : [];
};

const getFallbackTierForCode = (planCode, roleName = 'Student') => {
  const rolePlans = getRolePlanConfigs(roleName);
  const index = rolePlans.findIndex((plan) => plan.code === planCode);
  return index >= 0 ? index + 1 : null;
};

const normalizeDisplayedPlan = (plan, roleName = 'Student') => {
  if (!plan?.code) return plan;
  const displayConfig = getPlanDisplayConfig(plan.code, roleName);
  const fallbackTier = getFallbackTierForCode(plan.code, roleName);
  const normalizedPrice =
    plan.price != null
      ? plan.price
      : displayConfig?.price === 'Free'
        ? '0.00'
        : displayConfig?.price?.replace(/[^0-9.]/g, '') || null;

  return {
    ...plan,
    name: plan.name || displayConfig?.title || plan.code,
    title: displayConfig?.title || plan.title || plan.name || plan.code,
    price: normalizedPrice,
    period: plan.period || displayConfig?.period || '',
    duration: plan.duration || displayConfig?.duration || 'monthly',
    tier: typeof plan.tier === 'number' ? plan.tier : fallbackTier,
  };
};

const getPlanLabel = (plan, roleName = 'Student') => {
  if (!plan) return '';
  return normalizeDisplayedPlan(plan, roleName)?.name || '';
};

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Menu state: when returning from Stripe Checkout (success=true in URL), open Subscription tab so user sees purchase history.
  // Also honour explicit activeMenu passed via location.state (e.g. from dashboard banner).
  const [activeMenu, setActiveMenu] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    if (params.get('success') === 'true' || params.get('session_id')) return 'subscription';
    if (location.state && location.state.activeMenu) return location.state.activeMenu;
    if (location.state && location.state.message) return 'subscription';
    return 'details';
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // User data state
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState('');

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState('');
  const [plans, setPlans] = useState([]);
  const [planFeatures, setPlanFeatures] = useState({});
  const [subscribedPlanId, setSubscribedPlanId] = useState(null);
  const [subscribedPlanCode, setSubscribedPlanCode] = useState(null);
  const [hasConsumedStarter, setHasConsumedStarter] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    age: '',
    address: ''
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    autoHideDuration: 6000
  });
  
  // Teachers state (for students)
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loadingFamilyMembers, setLoadingFamilyMembers] = useState(false);
  const [relationshipTab, setRelationshipTab] = useState('teachers');
  const [exitDialog, setExitDialog] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  
  // Add-ons state
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [processingPurchase, setProcessingPurchase] = useState(false);
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [addonPromoCode, setAddonPromoCode] = useState('');
  const [loadingCart, setLoadingCart] = useState(false);
  const [cartDialog, setCartDialog] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  
  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [paymentPromoCode, setPaymentPromoCode] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState(null);
  const [subscriptionSubTab, setSubscriptionSubTab] = useState('plans');
  const [cancellingScheduled, setCancellingScheduled] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [studentPlans, setStudentPlans] = useState([]);
  const [currentPlanCodeStudent, setCurrentPlanCodeStudent] = useState(null);
  const [activeIndividualPlanCodeStudent, setActiveIndividualPlanCodeStudent] = useState(null);
  const [requestUpgradeLoading, setRequestUpgradeLoading] = useState(null);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(false);
  const [pendingBillingConfirmation, setPendingBillingConfirmation] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Load user data function
  const loadUserData = async () => {
    try {
      console.log('Loading profile data...');
      const token = localStorage.getItem('token');
      console.log('Auth token:', token ? 'Token exists' : 'No token found');
      
      const response = await authAPI.getProfile();
      console.log('Profile API response:', response);
      
      const userData = response.data;
      console.log('User data:', userData);
      
      setUserData(userData);
      setUserRole(userData.role_name || '');
      
      setFormData({
        name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email.split('@')[0],
        email: userData.email,
        phone: userData.phone_number || '',
        dateOfBirth: formatDate(userData.date_of_birth) || '',
        age: userData.age || '',
        address: userData.address || '',
      });
      
      // store subscription_type
      setSubscriptionType(userData.subscription_type || '');

      if (userData.profile_picture) {
        // Always use Django proxy endpoint regardless of whether URL is public or private
        const pictureUrl = `${BASE_URL}/api/accounts/media/profile-picture/${userData.id}/`;
        setPreview(pictureUrl);
        console.log('Profile picture preview URL:', pictureUrl);
        setSubscriptionType(userData.subscription_type || '');
      }
      else {
        console.warn('No profile_picture field in userData');
      }

      // Debug logging for user data
      console.log('User data for API calls:', {
        role_id: userData.role_id,
        role_name: userData.role_name,
        user_id: userData.id
      });
      
      // Batch API calls for better performance
      const [plansRes, subRes] = await Promise.all([
        api.get(`/accounts/pricing-plans/?role_id=${userData.role_id}`),
        api.get(`/accounts/user-subscriptions/?user=${userData.id}`)
      ]);

      // Process pricing plans
      let fetchedPlans = [];
      if (Array.isArray(plansRes.data)) {
        fetchedPlans = plansRes.data;
      } else if (plansRes.data && Array.isArray(plansRes.data.results)) {
        fetchedPlans = plansRes.data.results;
      }
      setPlans(fetchedPlans);

      // Process subscription data
      const subArray = Array.isArray(subRes.data) ? subRes.data : (subRes.data && Array.isArray(subRes.data.results) ? subRes.data.results : []);
      console.log('Subscription data:', subArray);
      
      // Store all user subscriptions
      setUserSubscriptions(subArray);

      // Determine the current subscription for UI:
      // - Prefer active subscriptions (is_active === true)
      // - Among actives, prefer the one with the latest start_date
      // - If none active, fall back to the latest by start_date
      let currentSubscription = null;
      if (subArray.length > 0) {
        const activeSelfManaged = getMostRecentSubscription(
          subArray,
          (sub) => sub.is_active === true && !sub.is_family_managed
        );
        const activeSubs = subArray.filter(sub => sub.is_active === true);
        if (activeSelfManaged) {
          currentSubscription = activeSelfManaged;
        } else if (activeSubs.length > 0) {
          currentSubscription = getMostRecentSubscription(activeSubs);
        } else {
          currentSubscription = getMostRecentSubscription(subArray);
        }
      }

      console.log('Current subscription chosen for UI:', {
        id: currentSubscription?.id,
        plan_code:
          currentSubscription?.pricing_plan?.code ||
          currentSubscription?.plan_code ||
          currentSubscription?.pricing_plan_code,
        is_active: currentSubscription?.is_active,
        payment_status: currentSubscription?.payment_status,
        start_date: currentSubscription?.start_date,
        end_date: currentSubscription?.end_date
      });

      if (currentSubscription) {
        setSubscription(currentSubscription);
        setSubscribedPlanId(getSubscriptionPlanId(currentSubscription));
        setSubscribedPlanCode(getSubscriptionPlanCode(currentSubscription));
      } else {
        setSubscription(null);
        setSubscribedPlanId(null);
        setSubscribedPlanCode(null);
      }
      
      // Check if user has consumed any starter plan
      const consumedStarter = subArray.some(sub => {
        const isStarterPlan = (sub.plan_code && sub.plan_code.endsWith('_starter')) || 
                             (sub.pricing_plan_code && sub.pricing_plan_code.endsWith('_starter'));
        const isInactive = !sub.is_active;
        console.log(`Subscription ${sub.id}: plan_code=${sub.plan_code}, is_active=${sub.is_active}, isStarter=${isStarterPlan}, isInactive=${isInactive}`);
        return isStarterPlan && isInactive;
      });
      
      // Also check if user is currently on a starter plan (which means they've consumed it)
      const currentStarterPlan = subArray.some(sub => {
        const isStarterPlan = (sub.plan_code && sub.plan_code.endsWith('_starter')) || 
                             (sub.pricing_plan_code && sub.pricing_plan_code.endsWith('_starter'));
        const isActive = sub.is_active;
        return isStarterPlan && isActive;
      });
      
      setHasConsumedStarter(consumedStarter || currentStarterPlan);

      // Load features in parallel for better performance
      const featurePromises = fetchedPlans.map(async (p) => {
        try {
          const featRes = await api.get(`${FEATURE_ACCESS}?pricing_plan=${p.id}`);
          const featArray = Array.isArray(featRes.data) ? featRes.data : (featRes.data && Array.isArray(featRes.data.results) ? featRes.data.results : []);
          return {
            planId: p.id,
            features: featArray.filter(fa => fa.is_enabled !== false).map(fa => fa.feature_label || fa.feature_name || fa.feature_code || 'Feature')
          };
        } catch(e) { 
          console.error('feature fetch', e);
          return { planId: p.id, features: [] };
        }
      });

      const featureResults = await Promise.all(featurePromises);
      const featureMap = {};
      featureResults.forEach(result => {
        featureMap[result.planId] = result.features;
      });
      setPlanFeatures(featureMap);

    } catch (error) {
      console.error('Error loading user data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load user data. Please refresh the page.',
        severity: 'error',
        autoHideDuration: 6000
      });
    }
  };

  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const response = await api.get(STUDENT_TEACHERS);
      console.log('Teachers response:', response.data);
      if (response.data.success) {
        setTeachers(response.data.teachers);
      } else {
        setTeachers([]);
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load teachers',
        severity: 'error',
        autoHideDuration: 6000
      });
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      setLoadingFamilyMembers(true);
      const response = await api.get(STUDENT_FAMILY);
      if (response.data.success) {
        setFamilyMembers(response.data.family_members || []);
      } else {
        setFamilyMembers([]);
      }
    } catch (error) {
      console.error('Error loading family members:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load family members',
        severity: 'error',
        autoHideDuration: 6000
      });
      setFamilyMembers([]);
    } finally {
      setLoadingFamilyMembers(false);
    }
  };

  const loadAddons = async () => {
    try {
      setLoadingAddons(true);
      console.log('Loading add-ons from:', ADDON_CATALOG);
      const response = await api.get(ADDON_CATALOG);
      console.log('Add-ons response:', response.data);
      // Extract the results array from the paginated response
      const addonsData = response.data.results || response.data;
      console.log('Setting addons to:', addonsData);
      setAddons(addonsData);
    } catch (error) {
      console.error('Error loading add-ons:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load add-ons',
        severity: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setLoadingAddons(false);
    }
  };

  const loadPurchaseHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get(ADDONS_HISTORY);
      // Extract the results array from the paginated response
      const historyData = response.data.results || response.data;
      setPurchaseHistory(historyData);
    } catch (error) {
      console.error('Error loading purchase history:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load purchase history',
        severity: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadEntitlements = async () => {
    try {
      setLoadingEntitlements(true);
      const response = await api.get(ENTITLEMENTS);
      // Extract the results array from the paginated response
      const entitlementsData = response.data.results || response.data;
      setEntitlements(entitlementsData);
    } catch (error) {
      console.error('Error loading entitlements:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load entitlements',
        severity: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setLoadingEntitlements(false);
    }
  };

  const handlePurchaseAddon = async () => {
    if (!selectedAddon || purchaseQuantity < 1) return;
    
    try {
      setProcessingPurchase(true);
      
      // Add item to cart first
      await addToCart(selectedAddon, purchaseQuantity);
      
      // Navigate to add-on payment page
      navigate('/addon-payment', {
        replace: true,
        state: {
          cartItems: [{
            addon_id: selectedAddon.id,
            label: selectedAddon.label,
            quantity: purchaseQuantity,
            unit_price: selectedAddon.unit_price,
            total_price: selectedAddon.unit_price * purchaseQuantity
          }],
          totalAmount: selectedAddon.unit_price * purchaseQuantity
        }
      });
    } catch (error) {
      console.error('Error purchasing add-on:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to purchase add-on',
        severity: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setProcessingPurchase(false);
    }
  };

  const loadCart = async () => {
    try {
      setLoadingCart(true);
      const response = await api.get(ADDONS_CART);
      setCart(response.data.items || []);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoadingCart(false);
    }
  };

  const addToCart = async (addon, quantity = 1) => {
    try {
      const response = await api.post(ADDONS_CART, {
        addon_id: addon.id,
        quantity: quantity
      });
      
      setSnackbar({
        open: true,
        message: 'Item added to cart!',
        severity: 'success',
        autoHideDuration: 3000
      });
      
      // Reload cart
      await loadCart();
    } catch (error) {
      console.error('Error adding to cart:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to add to cart',
        severity: 'error',
        autoHideDuration: 6000
      });
    }
  };

  const removeFromCart = async (addonId) => {
    try {
      await api.delete(ADDONS_CART, {
        data: { addon_id: addonId }
      });
      
      setSnackbar({
        open: true,
        message: 'Item removed from cart!',
        severity: 'success',
        autoHideDuration: 3000
      });
      
      // Reload cart
      await loadCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to remove from cart',
        severity: 'error',
        autoHideDuration: 6000
      });
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    try {
      setProcessingCheckout(true);
      const cartPayload = cart.map(item => ({
        addon_id: item.addon_id,
        quantity: item.quantity
      }));
      const data = await createCheckoutAddon(cartPayload, '/profile', '/profile', addonPromoCode || undefined);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      const cartItems = cart.map(item => ({
        addon_id: item.addon_id,
        label: item.label,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.unit_price * item.quantity
      }));
      const totalAmount = cartItems.reduce((sum, item) => sum + item.total_price, 0);
      navigate('/addon-payment', {
        replace: true,
        state: { cartItems, totalAmount }
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create checkout session',
        severity: 'error',
        autoHideDuration: 6000
      });
    } finally {
      setProcessingCheckout(false);
    }
  };

  // Exit from teacher relationship
  const handleExitTeacher = async (relationshipId) => {
    try {
              await api.post(`${STUDENT_EXIT_TEACHER}${relationshipId}/`);
      setSnackbar({
        open: true,
        message: 'Successfully exited from teacher relationship',
        severity: 'success'
      });
      // Reload teachers list
      loadTeachers();
    } catch (error) {
      console.error('Error exiting teacher relationship:', error);
      setSnackbar({
        open: true,
        message: 'Failed to exit teacher relationship. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleAcceptInvitation = async (invitationId, action = 'accept') => {
    console.log('handleAcceptInvitation called with:', { invitationId, action });
    try {
      const response = await api.post(STUDENT_TEACHERS, {
        invitation_id: invitationId,
        action: action
      });
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        // Mark that at least one invitation has been accepted, so the dashboard
        // can hide the "accept your teacher/parent invitation" banner.
        try {
          const invitesAcceptedKey = user ? `invitesAccepted:${user.id}` : null;
          if (invitesAcceptedKey && action === 'accept') {
            localStorage.setItem(invitesAcceptedKey, 'true');
          }
        } catch (e) {
          console.warn('Unable to persist invitesAccepted flag:', e);
        }
        // Reload teachers list
        loadTeachers();
      } else {
        setSnackbar({
          open: true,
          message: response.data.error || `Failed to ${action} invitation`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${action}ing invitation:`, error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || `Failed to ${action} invitation. Please try again.`,
        severity: 'error'
      });
    }
  };

  // Load user data on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await loadUserData();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const loadBillingSummary = async () => {
    try {
      setBillingSummaryLoading(true);
      const data = await getBillingSummary();
      setBillingSummary(data);
    } catch (e) {
      console.error('Failed to load billing summary', e);
      setBillingSummary(null);
    } finally {
      setBillingSummaryLoading(false);
    }
  };

  const isFamilyManagedStudentSubscription = Boolean(
    userRole === 'STUDENT' && (
      userData?.subscription_self_service_locked ||
      subscription?.is_family_managed ||
      billingSummary?.subscription?.is_family_managed
    )
  );
  const isAdultStudent = Boolean(userData?.is_adult_student);
  const addonPurchaseBlocked = Boolean(userData?.addon_purchase_blocked);
  const isParentManagedStudentAccount = Boolean(
    userRole === 'STUDENT' && (
      isFamilyManagedStudentSubscription ||
      (userData?.parent_email && !isAdultStudent)
    )
  );
  const managingFamilyName =
    userData?.parent_email ||
    subscription?.provisioned_by_family_name ||
    billingSummary?.subscription?.provisioned_by_family_name ||
    'your family';
  const activeSelfManagedStudentSubscription = getMostRecentSubscription(
    userSubscriptions,
    (sub) => sub.is_active === true && !sub.is_family_managed
  );
  const activeFamilyManagedStudentSubscription = getMostRecentSubscription(
    userSubscriptions,
    (sub) => sub.is_active === true && sub.is_family_managed
  );
  const activeIndividualStudentPlanCode =
    getSubscriptionPlanCode(activeSelfManagedStudentSubscription) || activeIndividualPlanCodeStudent;
  const activeFamilyStudentPlanCode = (() => {
    const directFamilyCode = getSubscriptionPlanCode(activeFamilyManagedStudentSubscription);
    if (directFamilyCode) return directFamilyCode;
    return typeof currentPlanCodeStudent === 'string' && currentPlanCodeStudent.startsWith('family_')
      ? currentPlanCodeStudent
      : null;
  })();
  const hasActiveIndividualStudentPlan = Boolean(
    userRole === 'STUDENT' &&
    activeIndividualStudentPlanCode &&
    !activeIndividualStudentPlanCode.startsWith('family_')
  );
  const hasActiveFamilyStudentPlan = Boolean(
    userRole === 'STUDENT' &&
    activeFamilyStudentPlanCode &&
    activeFamilyStudentPlanCode.startsWith('family_')
  );
  const studentPlanSelfServiceLocked = Boolean(
    userRole === 'STUDENT' &&
    !hasActiveIndividualStudentPlan &&
    (
      userData?.subscription_self_service_locked ||
      hasActiveFamilyStudentPlan ||
      billingSummary?.subscription?.is_family_managed ||
      (userData?.parent_email && !isAdultStudent)
    )
  );

  // Load student family plans when subscription tab and user is student
  const loadStudentPlans = async () => {
    try {
      const res = await api.get(STUDENT_PLANS);
      setStudentPlans(res.data?.plans || []);
      setCurrentPlanCodeStudent(res.data?.current_plan_code || null);
      setActiveIndividualPlanCodeStudent(res.data?.active_individual_plan_code || null);
    } catch (e) {
      setStudentPlans([]);
      setCurrentPlanCodeStudent(null);
      setActiveIndividualPlanCodeStudent(null);
    }
  };
  const handleRequestUpgrade = async (planCode) => {
    setRequestUpgradeLoading(planCode);
    try {
      await api.post(STUDENT_REQUEST_UPGRADE, { plan_code: planCode });
      setSnackbar({ open: true, message: 'Upgrade request sent. Your parent will be notified.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.error || 'Request failed.', severity: 'error' });
    } finally {
      setRequestUpgradeLoading(null);
    }
  };

  const renderPlanGridSection = ({
    sectionTitle = null,
    sectionPlans = [],
    currentSubscription = null,
    currentPlanCode = null,
    locked = false,
  }) => {
    if (!sectionPlans.length) return null;

    return (
      <Box sx={{ mb: 4 }}>
        {sectionTitle && (
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {sectionTitle}
          </Typography>
        )}
        <Grid container spacing={3} justifyContent="center">
          {sectionPlans.map(plan => {
            const roleName = (userRole && userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase()) || 'Student';
            const displayConfig = getPlanDisplayConfig(plan.code, roleName);
            const planTitle = displayConfig ? displayConfig.title : plan.name;
            const planPriceDisplay = displayConfig ? (displayConfig.price + displayConfig.period) : null;
            const isCurrentPlan =
              (currentPlanCode && currentPlanCode === plan.code) ||
              (getSubscriptionPlanId(currentSubscription) && getSubscriptionPlanId(currentSubscription) === plan.id);

            return (
              <Grid item xs={12} sm={6} md={4} key={`${sectionTitle || 'plans'}-${plan.id || plan.code}`}>
                <Card
                  variant={isCurrentPlan ? 'outlined' : undefined}
                  sx={{
                    borderColor: isCurrentPlan ? 'primary.main' : 'divider',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 400
                  }}
                >
                  <CardHeader
                    title={planTitle}
                    action={isCurrentPlan && (
                      <Chip
                        label={isPlanExpired(currentSubscription) ? "Expired" : "Subscribed"}
                        color={isPlanExpired(currentSubscription) ? "error" : "primary"}
                        size="small"
                      />
                    )}
                    sx={{ textAlign:'center', '& .MuiCardHeader-action': { m:0 } }}
                  />
                  {isCurrentPlan && currentSubscription && (
                    <Box sx={{ px: 2, pb: 1 }}>
                      {!(currentSubscription.scheduled_plan_label && currentSubscription.scheduled_change_date) && (
                        <>
                          {currentSubscription.auto_renew && !isPlanExpired(currentSubscription) && (
                            <Typography variant="body2" color="text.secondary" align="center">
                              Auto renews on {calculateNextRenewalDate(currentSubscription.effective_end_date || currentSubscription.end_date, currentSubscription.plan_duration)}
                            </Typography>
                          )}
                          {!currentSubscription.auto_renew && !isPlanExpired(currentSubscription) && currentSubscription.end_date && (
                            <Typography variant="body2" color="warning.main" align="center">
                              Expires on {formatDate(currentSubscription.effective_end_date || currentSubscription.end_date)}
                            </Typography>
                          )}
                        </>
                      )}

                      {isPlanExpired(currentSubscription) && (
                        <Typography variant="body2" color="error" align="center">
                          Expired on {formatDate(currentSubscription.end_date)}
                        </Typography>
                      )}

                      {currentSubscription.scheduled_plan_label &&
                        currentSubscription.scheduled_change_date && (
                          <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                            <Typography variant="body2" color="warning.main" align="center">
                              Scheduled change: downgrades to {currentSubscription.scheduled_plan_label} on {formatDate(currentSubscription.scheduled_change_date)}
                            </Typography>
                            {!locked && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={handleCancelScheduledDowngrade}
                                disabled={cancellingScheduled}
                                sx={{ mt: 0.5 }}
                              >
                                {cancellingScheduled ? 'Cancelling…' : 'Cancel downgrade'}
                              </Button>
                            )}
                          </Box>
                      )}
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ minHeight: 72, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', mb: 2 }}>
                      <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
                        {planPriceDisplay != null
                          ? planPriceDisplay
                          : <>{plan.currency || '$'}{plan.price ?? '--'}<Typography component="span" variant="subtitle2">/mo</Typography></>
                        }
                      </Typography>
                    </Box>

                    {!displayConfig && (() => {
                      const limits = [
                        { label: 'No of Tests', value: plan.test_limit },
                        { label: 'No of Students', value: plan.student_limit },
                        { label: 'No of Children', value: plan.child_limit },
                      ].filter(l => l.value && l.value > 0);
                      if (!limits.length) return null;
                      return (
                        <Box mb={2}>
                          {limits.map(l => (
                            <Typography key={l.label} variant="body2" align="center">
                              {l.label}: {l.value}
                            </Typography>
                          ))}
                        </Box>
                      );
                    })()}

                    {!displayConfig && plan.description && (
                      <Typography variant="body2" color="text.secondary" align="center" mb={2}>
                        {plan.description}
                      </Typography>
                    )}

                    <Box sx={{ flexGrow: 1 }}>
                      <List dense>
                        {displayConfig ? (
                          Object.entries(displayConfig.features).map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                {value === 'Yes' ? (
                                  <CheckCircle fontSize="small" color="success" />
                                ) : value === 'X' ? (
                                  <HighlightOff fontSize="small" color="error" />
                                ) : null}
                              </ListItemIcon>
                              <ListItemText
                                primary={value === 'Yes' || value === 'X' ? label : `${label}: ${value}`}
                              />
                            </ListItem>
                          ))
                        ) : (
                          (planFeatures[plan.id] || []).map(feat => (
                            <ListItem key={feat} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                <CheckCircle fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText primary={feat} />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'center', pb: 2, pt: 0 }}>
                    {isCurrentPlan ? (
                      locked ? (
                        <Button variant="outlined" color="primary" disabled fullWidth>
                          Managed by parent
                        </Button>
                      ) : currentSubscription?.scheduled_plan_label && currentSubscription?.scheduled_change_date ? (
                        <Button variant="outlined" color="primary" disabled fullWidth>
                          Current Plan
                        </Button>
                      ) : !requiresPayment(plan.code) ? (
                        <Button variant="outlined" color="primary" disabled fullWidth>
                          Subscribed
                        </Button>
                      ) : isPlanExpired(currentSubscription) ? (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handlePlanChange(normalizeDisplayedPlan(plan, userRole || 'Student'))}
                          fullWidth
                        >
                          Renew now
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color={currentSubscription?.auto_renew ? 'error' : 'success'}
                          onClick={() => handleToggleAutoRenew(plan)}
                          fullWidth
                        >
                          {currentSubscription?.auto_renew ? 'Cancel Auto-Renew' : 'Activate Auto-Renew'}
                        </Button>
                      )
                    ) : (() => {
                      const scheduledPlanId = currentSubscription?.scheduled_plan?.id ?? currentSubscription?.scheduled_plan;
                      const isScheduledTarget = currentSubscription?.scheduled_plan_label && currentSubscription?.scheduled_change_date && (plan.id === scheduledPlanId || plan.name === currentSubscription?.scheduled_plan_label);
                      if (isScheduledTarget) {
                        return (
                          <Button variant="outlined" color="primary" disabled fullWidth>
                            Starts on {formatDate(currentSubscription?.scheduled_change_date)}
                          </Button>
                        );
                      }
                      const isPaidPlan = requiresPayment(plan.code);
                      const isFreeChampionPlan = isChampionsUser && !isPaidPlan;
                      return (
                        <Button
                          variant="outlined"
                          startIcon={!isFreeChampionPlan ? <Payment /> : undefined}
                          onClick={!isFreeChampionPlan && !locked ? () => handlePlanChange(normalizeDisplayedPlan(plan, userRole || 'Student')) : undefined}
                          disabled={isFreeChampionPlan || locked}
                          fullWidth
                        >
                          {locked
                            ? 'Managed by parent'
                            : isFreeChampionPlan
                            ? 'Current plan'
                            : (isPaidPlan ? `Switch to ${planTitle}` : 'Switch to Free')}
                        </Button>
                      );
                    })()}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Load add-ons data when My Subscription is selected (so Add Ons tab has data ready)
  useEffect(() => {
    if (activeMenu === 'subscription') {
      loadAddons();
      loadEntitlements();
      loadCart();
      loadPurchaseHistory();
      loadBillingSummary();
      if (userRole === 'STUDENT') loadStudentPlans();
    }
  }, [activeMenu, userRole]);

  // Handle return from Checkout or Portal: never trust URL alone; refresh backend state and show pending until reflected
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const sessionId = urlParams.get('session_id');

    if (success === 'true' || sessionId) {
      setPendingBillingConfirmation(true);
      setSnackbar({
        open: true,
        message: sessionId
          ? 'Payment received. Confirming your subscription…'
          : 'Payment successful! Your purchase is being confirmed.',
        severity: 'success',
        autoHideDuration: 6000
      });
      const refresh = async () => {
        try {
          await api.post(ADDONS_CART_CLEAR);
        } catch (_) {
          // Non-blocking: cart clear best-effort
        }
        await loadUserData();
        await loadBillingSummary();
        if (activeMenu === 'subscription') {
          loadEntitlements();
          loadPurchaseHistory();
          loadCart();
        }
      };
      refresh().then(() => {
        setTimeout(() => setPendingBillingConfirmation(false), 2000);
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      setSnackbar({
        open: true,
        message: 'Payment was canceled.',
        severity: 'info',
        autoHideDuration: 4000
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Load relationship data when menu changes to the student relationship section.
  useEffect(() => {
    if (activeMenu === 'teachers' && userRole === 'STUDENT') {
      if (!teachers || teachers.length === 0) {
        loadTeachers();
      }
      if (!familyMembers || familyMembers.length === 0) {
        loadFamilyMembers();
      }
    }
  }, [activeMenu, userRole]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleChangePassword = () => {
    navigate('/settings/change-password');
  };

  const handlePlanChange = async (plan) => {
    if (studentPlanSelfServiceLocked) {
      setSnackbar({
        open: true,
        message: 'This family plan is managed by the parent account.',
        severity: 'info',
        autoHideDuration: 5000
      });
      return;
    }
    try {
      // Always allow switching to free plans
      if (!requiresPayment(plan.code) && (plan.code.endsWith('_starter') || plan.code === 'teacher_starter' || plan.code === 'student_starter')) {
        // For free plans, proceed directly without additional validation
        setSelectedPlan(plan);
        setPaymentDialog(true);
        return;
      }

      // Get current active subscription
      const currentSubscription =
        subscription && subscription.is_active
          ? subscription
          : userSubscriptions.find(sub => sub.is_active === true);
      const currentPlanCode = getSubscriptionPlanCode(currentSubscription) || subscribedPlanCode;
      const currentPlanId = getSubscriptionPlanId(currentSubscription) || subscribedPlanId;
      const currentPlan = normalizeDisplayedPlan(
        plans.find(p => p.code === currentPlanCode || p.id === currentPlanId) || {
          code: currentPlanCode,
          id: currentPlanId,
          name: currentSubscription?.pricing_plan?.name || currentSubscription?.plan_label,
          tier: currentSubscription?.pricing_plan?.tier || null,
        },
        userRole || 'Student'
      );
      
      console.log('Current subscription detection:', {
        userSubscriptions: userSubscriptions.map(sub => ({
          id: sub.id,
          is_active: sub.is_active,
          pricing_plan: sub.pricing_plan,
          plan_code: getSubscriptionPlanCode(sub)
        })),
        currentSubscription: currentSubscription ? {
          id: currentSubscription.id,
          is_active: currentSubscription.is_active,
          pricing_plan: currentSubscription.pricing_plan,
          plan_code: getSubscriptionPlanCode(currentSubscription)
        } : null,
        currentPlan: currentPlan ? {
          id: currentPlan.id,
          name: currentPlan.name,
          code: currentPlan.code,
          tier: currentPlan.tier
        } : null
      });

      // Check if user has a previous subscription for the target plan
      const previousTargetSubscription = userSubscriptions.find(sub => {
        // Try different possible field names for plan code
        const subPlanCode = getSubscriptionPlanCode(sub);
        const targetPlanCode = plan.code;
        
        // Also try matching by plan ID as fallback
        const subPlanId = getSubscriptionPlanId(sub);
        const targetPlanId = plan.id;
        
        const hasValidEndDate = sub.end_date && new Date(sub.end_date) > new Date();
        const matchesTargetPlan = subPlanCode === targetPlanCode || subPlanId === targetPlanId;
        const isNotCurrent = sub.id !== currentSubscription?.id;
        
        // For reactivation, also consider inactive subscriptions that were previously paid
        const wasPaidSubscription = sub.payment_status === 'active' || 
                                  (getSubscriptionPlanCode(sub) && !getSubscriptionPlanCode(sub)?.endsWith('_starter'));
        
        console.log(`Checking subscription ${sub.id}:`, {
          subPlanCode,
          targetPlanCode,
          subPlanId,
          targetPlanId,
          hasValidEndDate,
          matchesTargetPlan,
          isNotCurrent,
          wasPaidSubscription,
          end_date: sub.end_date,
          end_date_parsed: sub.end_date ? new Date(sub.end_date) : null,
          current_time: new Date(),
          is_active: sub.is_active,
          payment_status: sub.payment_status
        });
        
        // For reactivation, we want to find any previous paid subscription for this plan
        // that was active at some point, regardless of current end date
        return matchesTargetPlan && isNotCurrent && wasPaidSubscription;
      });

      console.log('Previous target subscription search:', {
        targetPlanCode: plan.code,
        userSubscriptions: userSubscriptions.map(sub => ({
          id: sub.id,
          plan_code: getSubscriptionPlanCode(sub),
          is_active: sub.is_active,
          payment_status: sub.payment_status,
          end_date: sub.end_date,
          end_date_future: sub.end_date ? new Date(sub.end_date) > new Date() : false,
          matches_target: getSubscriptionPlanCode(sub) === plan.code,
          is_current: sub.id === currentSubscription?.id
        })),
        foundPreviousTarget: !!previousTargetSubscription,
        previousTargetDetails: previousTargetSubscription ? {
          id: previousTargetSubscription.id,
          plan_code: getSubscriptionPlanCode(previousTargetSubscription),
          is_active: previousTargetSubscription.is_active,
          payment_status: previousTargetSubscription.payment_status,
          end_date: previousTargetSubscription.end_date,
          days_left: previousTargetSubscription.end_date ? 
            Math.ceil((new Date(previousTargetSubscription.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0
        } : null
      });

      // Determine if this is an upgrade or downgrade
      const isUpgrade = currentPlan && plan.tier > currentPlan.tier;
      const isDowngrade = currentPlan && plan.tier < currentPlan.tier;
      const isSameTier = currentPlan && plan.tier === currentPlan.tier;

      // Calculate days left in current plan
      const daysLeft = currentSubscription?.end_date ? 
        Math.ceil((new Date(currentSubscription.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

      console.log('Plan switch analysis:', {
        currentPlan: currentPlan?.name,
        targetPlan: plan.name,
        isUpgrade,
        isDowngrade,
        daysLeft,
        hasPreviousTarget: !!previousTargetSubscription,
        previousTargetEndDate: previousTargetSubscription?.end_date,
        previousTargetDaysLeft: previousTargetSubscription?.end_date ? 
          Math.ceil((new Date(previousTargetSubscription.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0,
        allUserSubscriptions: userSubscriptions.map(sub => ({
          id: sub.id,
          plan_code: getSubscriptionPlanCode(sub),
          is_active: sub.is_active,
          payment_status: sub.payment_status,
          end_date: sub.end_date,
          days_left: sub.end_date ? Math.ceil((new Date(sub.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0
        }))
      });

      // Handle different scenarios based on the table rules
      if (isUpgrade) {
        setSelectedPlan(plan);
        setUpgradePreview(null);
        setPaymentDialog(true);
        setUpgradePreviewLoading(true);
        getUpgradePreview(plan.code)
          .then((data) => setUpgradePreview(data))
          .catch(() => setUpgradePreview(null))
          .finally(() => setUpgradePreviewLoading(false));
        return;
      } else if (isDowngrade) {
        // Downgrading to lower tier plan – confirm with the user and clearly
        // explain that the downgrade will take effect at the end of the current
        // billing cycle when applicable.
        const effectiveDate =
          currentSubscription && currentSubscription.end_date
            ? new Date(currentSubscription.end_date)
            : new Date();

        setPendingDowngrade({
          plan,
          currentSubscription,
          previousTargetSubscription,
          daysLeft,
          effectiveDate
        });
        setDowngradeDialogOpen(true);
        return;
      } else if (isSameTier) {
        // Same tier switch (e.g., Pro to Pro with different features)
        if (previousTargetSubscription) {
          try {
            await billingReactivateSubscription(previousTargetSubscription.id);
            setSnackbar({
              open: true,
              message: 'Plan switched successfully',
              severity: 'success',
              autoHideDuration: 5000
            });
            setActiveMenu('subscription');
            await loadUserData();
            await loadBillingSummary();
            return;
          } catch (error) {
            console.error('Error switching plan:', error);
            const backendMessage = error.response?.data?.error;
            setSnackbar({
              open: true,
              message: backendMessage || 'Failed to switch plan. Please try again.',
              severity: 'error',
              autoHideDuration: 6000
            });
            return;
          }
        } else {
          // No valid previous subscription, redirect to payment
          setSelectedPlan(plan);
          setPaymentDialog(true);
          return;
        }
      } else {
        // No current plan (first subscription) or cross-role switch
        if (previousTargetSubscription) {
          try {
            await billingReactivateSubscription(previousTargetSubscription.id);
            setSnackbar({
              open: true,
              message: 'Subscription activated successfully',
              severity: 'success',
              autoHideDuration: 5000
            });
            setActiveMenu('subscription');
            await loadUserData();
            await loadBillingSummary();
            return;
          } catch (error) {
            console.error('Error activating subscription:', error);
            const backendMessage = error.response?.data?.error;
            setSnackbar({
              open: true,
              message: backendMessage || 'Failed to activate subscription. Please try again.',
              severity: 'error',
              autoHideDuration: 6000
            });
            return;
          }
        } else {
          // No valid previous subscription, redirect to payment
          setSelectedPlan(plan);
          setPaymentDialog(true);
          return;
        }
      }

    } catch (error) {
      console.error('Error validating plan change:', error);
      setSnackbar({
        open: true,
        message: 'Error validating plan change. Please try again.',
        severity: 'error',
        autoHideDuration: 6000
      });
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentDialog(false);
    setSelectedPlan(null);
    setChangeReason('');
    setSnackbar({
      open: true,
      message: 'Subscription updated successfully!',
      severity: 'success',
      autoHideDuration: 5000
    });
    setActiveMenu('subscription');
    loadUserData();
  };

  const executeDowngrade = async () => {
    if (studentPlanSelfServiceLocked) {
      setSnackbar({
        open: true,
        message: 'This family plan is managed by the parent account.',
        severity: 'info',
        autoHideDuration: 5000
      });
      return;
    }
    if (!pendingDowngrade) return;
    const { plan } = pendingDowngrade;
    try {
      const data = await billingChangePlan(plan.code, 'Downgrade from profile');
      setSnackbar({
        open: true,
        message: data?.message || 'Downgrade scheduled for the next billing cycle.',
        severity: 'success',
        autoHideDuration: 5000
      });
      setDowngradeDialogOpen(false);
      setPendingDowngrade(null);
      setActiveMenu('subscription');
      await loadUserData();
      await loadBillingSummary();
    } catch (error) {
      console.error('Error scheduling downgrade:', error);
      const backendMessage = error.response?.data?.error;
      setSnackbar({
        open: true,
        message: backendMessage || 'Failed to schedule downgrade. Please try again.',
        severity: 'error',
        autoHideDuration: 6000
      });
    }
  };

  const handlePaymentCancel = () => {
    setPaymentDialog(false);
    setSelectedPlan(null);
    setUpgradePreview(null);
    setChangeReason('');
    setPaymentPromoCode('');
  };

  const handlePaymentError = (error) => {
    setSnackbar({
      open: true,
      message: `Payment failed: ${error}`,
      severity: 'error',
      autoHideDuration: 8000
    });
    setIsProcessingPayment(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'File size should be less than 5MB',
          severity: 'error'
        });
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        setSnackbar({
          open: true,
          message: 'Only image files are allowed',
          severity: 'error'
        });
        return;
      }
      
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }
    
    // Phone validation (if provided)
    if (formData.phone && !/^[+\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
      isValid = false;
    }
    
    // Date of birth validation (if provided)
    if (formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      const minAgeDate = new Date(
        today.getFullYear() - 5, 
        today.getMonth(), 
        today.getDate()
      );
      
      if (isNaN(dob.getTime())) {
        newErrors.dateOfBirth = 'Invalid date';
        isValid = false;
      } else if (dob > minAgeDate) {
        newErrors.dateOfBirth = 'You must be at least 5 years old';
        isValid = false;
      }
    }
    
    // Age validation (if provided)
    if (formData.age) {
      const age = parseInt(formData.age);
      if (isNaN(age) || age < 0 || age > 150) {
        newErrors.age = 'Age must be between 0 and 150';
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form',
        severity: 'error',
        autoHideDuration: 5000
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Debug: Log the current form data
      console.log('Form data before processing:', formData);
      
      // Split name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      // Prepare form data for submission
      const formDataToSend = new FormData();
      
      // Always include these fields
      formDataToSend.append('first_name', firstName);
      formDataToSend.append('last_name', lastName);
      formDataToSend.append('email', formData.email);
      
      // Conditionally append optional fields if they have values
      if (formData.phone) formDataToSend.append('phone_number', formData.phone);
      if (formData.dateOfBirth) formDataToSend.append('date_of_birth', formData.dateOfBirth);
      if (formData.age) formDataToSend.append('age', formData.age);
      if (formData.address) formDataToSend.append('address', formData.address);
      if (selectedFile) {
        console.log('Appending profile picture file:', selectedFile);
        formDataToSend.append('profile_picture', selectedFile);
      }
      
      // Log the form data being sent
      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }
      
      console.log('Sending profile update to:', `${BASE_URL || ''}/api/accounts/profile/`);
      
      // Make the API call to update the profile
      const token = localStorage.getItem('token');
      console.log('Using auth token:', token ? 'Token exists' : 'No token found');
      
      const response = await authAPI.updateProfile(formDataToSend);
      
      console.log('Profile update response:', response);
      
      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }
      
      // Update the local state with the response data
      const updatedUser = response.data;
      
      // Update the auth token if it was refreshed
      if (response.data.token) {
        console.log('Updating auth token');
        localStorage.setItem('token', response.data.token);
      }
      
      // Update the form data with the response
      setFormData(prev => ({
        ...prev,
        name: `${updatedUser.first_name || ''} ${updatedUser.last_name || ''}`.trim() || updatedUser.email.split('@')[0],
        email: updatedUser.email,
        phone: updatedUser.phone_number || '',
        dateOfBirth: formatDate(updatedUser.date_of_birth) || '',
        age: updatedUser.age || '',
        address: updatedUser.address || '',
      }));
      
      // Update profile picture if it was changed
      if (updatedUser.profile_picture) {
        const pictureUrl = updatedUser.profile_picture.startsWith('http') 
          ? updatedUser.profile_picture 
          : `${BASE_URL || ''}${updatedUser.profile_picture}`;
        setPreview(pictureUrl);
      }
      
      // Clear the selected file
      setSelectedFile(null);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success',
        autoHideDuration: 5000
      });
      
    } catch (error) {
      console.error('Error updating profile:', error);
      let errorMessage = 'Failed to update profile. Please try again.';
      
      // Handle validation errors from the API
      if (error.response && error.response.data) {
        const apiErrors = error.response.data;
        if (typeof apiErrors === 'object') {
          // Convert API errors to form errors
          const newErrors = {};
          Object.entries(apiErrors).forEach(([field, messages]) => {
            // Map backend field names to frontend field names
            const frontendField = field === 'class_name' ? 'class' : field;
            newErrors[frontendField] = Array.isArray(messages) ? messages[0] : messages;
          });
          setErrors(newErrors);
          errorMessage = 'Please correct the errors in the form.';
        } else if (typeof apiErrors === 'string') {
          errorMessage = apiErrors;
        }
      } else if (error.message === 'Network Error') {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 10000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelScheduledDowngrade = async () => {
    try {
      setCancellingScheduled(true);
      const response = await api.post(SUBSCRIPTION_CANCEL_SCHEDULED_DOWNGRADE);
      const message = response.data?.message || 'Scheduled downgrade cancelled. You will remain on your current plan.';
      setSnackbar({ open: true, message, severity: 'success', autoHideDuration: 5000 });
      await loadUserData();
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to cancel scheduled downgrade.';
      setSnackbar({ open: true, message: msg, severity: 'error', autoHideDuration: 6000 });
    } finally {
      setCancellingScheduled(false);
    }
  };

  const handleToggleAutoRenew = async (plan) => {
    if (studentPlanSelfServiceLocked) {
      setSnackbar({
        open: true,
        message: 'This family plan is managed by the parent account.',
        severity: 'info',
        autoHideDuration: 5000
      });
      return;
    }
    try {
      const response = await api.patch(`${SUBSCRIPTION_TOGGLE_AUTO_RENEW}${subscription.id}/`);
      
      // Update local state immediately for better UX
      setSubscription({
        ...subscription,
        auto_renew: !subscription.auto_renew
      });
      
      setSnackbar({
        open: true,
        message: response.data.message,
        severity: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle auto-renew',
        severity: 'error',
        autoHideDuration: 3000
      });
    }
  };

  const isChampionsUser = (userRole || '').toUpperCase() === 'CHAMPIONS';

  const menuItems = [
    { id: 'details', label: 'My Details', icon: <Person /> },
  ];

  // Add My Subscription for all profiles except ADMIN (Add-ons is a tab under My Subscription)
  if (userRole !== 'ADMIN') {
    menuItems.push({ id: 'subscription', label: 'My Subscription', icon: <CreditCard /> });
  }


  const renderMenuContent = () => {
    switch (activeMenu) {
      case 'details':
        return (
          <Paper elevation={2} sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {/* Left Column - Profile Picture */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        src={preview || '/default-avatar.png'}
                        sx={{
                          width: 150,
                          height: 150,
                          border: '3px solid',
                          borderColor: 'primary.main',
                          mb: 2
                        }}
                      />
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="profile-picture-upload"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="profile-picture-upload">
                        <IconButton
                          color="primary"
                          aria-label="upload picture"
                          component="span"
                          sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 10,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <PhotoCamera />
                        </IconButton>
                      </label>
                    </Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {formData.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.email}
                    </Typography>
                  </Box>
                </Grid>

                {/* Right Column - Form Fields */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Tooltip title="Contact support to change email" arrow placement="top">
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          InputProps={{ readOnly: true }}
                          error={!!errors.email}
                          helperText={errors.email}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                      />
                    </Grid>
                    {/* Date of Birth field hidden - may be used in future
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        name="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth}
                      />
                    </Grid>
                    */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Age"
                        name="age"
                        type="number"
                        value={formData.age}
                        onChange={handleChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={!!errors.age}
                        helperText={errors.age}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        multiline
                        rows={3}
                        value={formData.address}
                        onChange={handleChange}
                        error={!!errors.address}
                        helperText={errors.address}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={() => navigate(-1)}
                          disabled={isSubmitting}
                          sx={{ minWidth: 120 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<VpnKeyIcon />}
                          onClick={handleChangePassword}
                          disabled={isSubmitting}
                          sx={{ minWidth: 180 }}
                        >
                          Change Password
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          sx={{ minWidth: 180 }}
                        >
                          {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Paper>
        );

      case 'subscription':
        return (
          <Box>
            {pendingBillingConfirmation && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirming your payment with the server. This may take a few seconds. Do not leave this page.
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                alignItems: 'center',
                mb: 2,
                gap: 1
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={portalLoading ? <CircularProgress size={16} /> : <CreditCard />}
                disabled={portalLoading || studentPlanSelfServiceLocked}
                aria-label="Manage billing (opens in new tab)"
                onClick={async () => {
                  if (studentPlanSelfServiceLocked) return;
                  setPortalLoading(true);
                  try {
                    const data = await createPortalSession('/profile');
                    if (data?.url) {
                      window.open(data.url, '_blank', 'noopener,noreferrer');
                      return;
                    }
                  } catch (e) {
                    setSnackbar({
                      open: true,
                      message: e.response?.data?.error || 'Could not open billing portal',
                      severity: 'error',
                      autoHideDuration: 6000
                    });
                  } finally {
                    setPortalLoading(false);
                  }
                }}
                sx={{
                  minHeight: { xs: 44, sm: undefined },
                  flex: { xs: 1, sm: 'none' },
                  maxWidth: { xs: '100%', sm: 'none' }
                }}
              >
                Manage billing
              </Button>
            </Box>
            <Tabs
              value={subscriptionSubTab}
              onChange={(_, v) => setSubscriptionSubTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label="Plans" value="plans" />
              {!isChampionsUser && <Tab label="Add Ons" value="addons" />}
              {!isChampionsUser && <Tab label="History" value="history" />}
            </Tabs>
            {isParentManagedStudentAccount && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {hasActiveIndividualStudentPlan
                  ? `This account also has a family plan managed by your parent ${managingFamilyName}.`
                  : `This account is managed by your parent ${managingFamilyName}.`}
              </Alert>
            )}
            {subscriptionSubTab === 'plans' && (
            <>
            {userRole === 'STUDENT' && !isParentManagedStudentAccount && (
              <Alert severity={isAdultStudent ? 'success' : 'info'} sx={{ mb: 2 }}>
                {isAdultStudent
                  ? 'This account is marked as an adult student and can self-manage eligible student purchases.'
                  : 'This account is not marked as an adult student. Add-ons must be managed by the parent through the family plan.'}
              </Alert>
            )}
            {plans.length > 0 ? (
              userRole === 'STUDENT' ? (
                <>
                  {hasActiveIndividualStudentPlan && renderPlanGridSection({
                    sectionTitle: hasActiveFamilyStudentPlan ? 'Individual Plan' : null,
                    sectionPlans: (PRICING_PLANS.Student || []).map(cfg => {
                      const matchingPlan = plans.find(p => p.code === cfg.code) || {};
                      return normalizeDisplayedPlan({ ...matchingPlan, code: cfg.code }, 'Student');
                    }),
                    currentSubscription: activeSelfManagedStudentSubscription || subscription,
                    currentPlanCode: activeIndividualStudentPlanCode || subscribedPlanCode,
                    locked: false,
                  })}
                  {hasActiveFamilyStudentPlan && renderPlanGridSection({
                    sectionTitle: hasActiveIndividualStudentPlan ? 'Family Plan' : null,
                    sectionPlans: studentPlans.map(plan => normalizeDisplayedPlan(plan, 'Parent')),
                    currentSubscription: activeFamilyManagedStudentSubscription || {
                      plan_code: activeFamilyStudentPlanCode,
                      subscription_status: 'active',
                      payment_completed: true,
                      is_active: true,
                      is_family_managed: true,
                      provisioned_by_family_name: managingFamilyName,
                    },
                    currentPlanCode: activeFamilyStudentPlanCode || currentPlanCodeStudent,
                    locked: true,
                  })}
                  {!hasActiveFamilyStudentPlan && !hasActiveIndividualStudentPlan && renderPlanGridSection({
                    sectionPlans: (PRICING_PLANS.Student || []).map(cfg => {
                      const matchingPlan = plans.find(p => p.code === cfg.code) || {};
                      return normalizeDisplayedPlan({ ...matchingPlan, code: cfg.code }, 'Student');
                    }),
                    currentSubscription: subscription,
                    currentPlanCode: subscribedPlanCode,
                    locked: studentPlanSelfServiceLocked,
                  })}
                </>
              ) : (
                renderPlanGridSection({
                  sectionPlans: (userRole === 'PARENT'
                    ? (PRICING_PLANS.Parent || []).map(cfg => {
                        const matchingPlan = plans.find(p => p.code === cfg.code) || {};
                        return normalizeDisplayedPlan({ ...matchingPlan, code: cfg.code }, 'Parent');
                      })
                    : plans.map(plan => normalizeDisplayedPlan(plan, userRole || 'Student'))),
                  currentSubscription: subscription,
                  currentPlanCode: subscribedPlanCode,
                  locked: false,
                })
              )
            ) : (
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No subscription plans available
                </Typography>
              </Paper>
            )}
            </>
            )}
            {!isChampionsUser && subscriptionSubTab === 'history' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Only the last {HISTORY_MAX_ENTRIES} history entries are maintained for each section below.
              </Typography>
              {/* Subscription History */}
              <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Subscription History
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Last {HISTORY_MAX_ENTRIES} subscription records.
                </Typography>
                {userSubscriptions.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Plan</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Start Date</TableCell>
                            <TableCell>End Date</TableCell>
                            <TableCell>Auto Renew</TableCell>
                            <TableCell>Payment Status</TableCell>
                            <TableCell>Change Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userSubscriptions
                            .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                            .slice(0, HISTORY_MAX_ENTRIES)
                            .map((sub) => (
                              <TableRow key={sub.id}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {sub.pricing_plan?.name || sub.plan_label || 'Unknown Plan'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {sub.pricing_plan?.code || sub.plan_code || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sub.is_active ? "Active" : "Inactive"} 
                                    color={sub.is_active ? "success" : "default"} 
                                    size="small" 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(sub.start_date)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {sub.end_date ? formatDate(sub.end_date) : 'No end date'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sub.auto_renew ? "ON" : "OFF"} 
                                    color={sub.auto_renew ? "primary" : "default"} 
                                    size="small" 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sub.payment_status || 'N/A'} 
                                    color={
                                      sub.payment_status === 'active' ? 'success' : 
                                      sub.payment_status === 'unpaid' ? 'error' : 
                                      'default'
                                    } 
                                    size="small" 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {sub.change_reason || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center">
                    No subscription history found
                  </Typography>
                )}
              </Paper>

              {/* Add-on Purchase History */}
              <Paper elevation={2} sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Add-on Purchase History
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Last {HISTORY_MAX_ENTRIES} add-on purchases.
                </Typography>
                {loadingHistory ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                    <CircularProgress />
                  </Box>
                ) : (() => {
                  const sortedAddonHistory = [...purchaseHistory]
                    .sort((a, b) => new Date(b.purchased_at || 0) - new Date(a.purchased_at || 0))
                    .slice(0, HISTORY_MAX_ENTRIES);
                  return sortedAddonHistory.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Add-on</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedAddonHistory.map((purchase) => (
                            <TableRow key={purchase.id}>
                              <TableCell>{purchase.addon_label}</TableCell>
                              <TableCell>{purchase.quantity}</TableCell>
                              <TableCell>${purchase.price}</TableCell>
                              <TableCell>{formatDate(purchase.purchased_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No add-on purchase history found
                    </Typography>
                  );
                })()}
              </Paper>
            </Box>
            )}
            {!isChampionsUser && subscriptionSubTab === 'addons' && (
            <Box>
              {/* Add-ons: 3 cards — Remaining (entitlements), Add Tests, Add Assisted Tests. Desktop: side-by-side; mobile: stacked */}
              <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>Add-ons & entitlements</Typography>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ShoppingCart />}
                    onClick={() => setCartDialog(true)}
                    disabled={cart.length === 0 || addonPurchaseBlocked}
                    sx={{ minHeight: 44 }}
                  >
                    Cart ({cart.length})
                  </Button>
                </Box>
                {addonPurchaseBlocked && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Add-ons can be viewed here, but only your parent can purchase them for this account.
                  </Alert>
                )}
                {(loadingEntitlements || loadingAddons) ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="220px">
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {/* Card 1: Tests remaining — circular progress + breakdown */}
                    <Grid item xs={12} md={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          overflow: 'hidden'
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          {entitlements ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                              <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={
                                    (() => {
                                      const remaining = Math.max(0, entitlements.remaining?.tests ?? 0);
                                      const sub = entitlements.subscription?.limits?.test_limit ?? 0;
                                      const addon = (entitlements.addon_entitlements?.self_test ?? 0) + (entitlements.addon_entitlements?.student_assisted_test ?? 0) + (entitlements.addon_entitlements?.teacher_assisted_test ?? 0);
                                      const total = sub + addon;
                                      if (total <= 0) return 100;
                                      return Math.min(100, Math.round((remaining / total) * 100));
                                    })()
                                  }
                                  size={100}
                                  thickness={6}
                                  sx={{ color: 'primary.main' }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="h5" fontWeight="bold" component="span" sx={{ lineHeight: 1 }}>
                                    {Math.max(0, entitlements.remaining?.tests ?? 0)}
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5, opacity: 0.9 }}>
                                    TESTS
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {Math.max(0, entitlements.remaining?.tests ?? 0)} Tests Remaining
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {entitlements.subscription?.limits?.test_limit ?? 0} Subscription
                                  {(entitlements.addon_entitlements?.self_test > 0 || entitlements.addon_entitlements?.student_assisted_test > 0 || entitlements.addon_entitlements?.teacher_assisted_test > 0) && (
                                    <> + {(entitlements.addon_entitlements?.self_test ?? 0) + (entitlements.addon_entitlements?.student_assisted_test ?? 0) + (entitlements.addon_entitlements?.teacher_assisted_test ?? 0)} Add-on</>
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                  Your current balance for assessment.
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Unable to load</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    {/* All available add-ons for the user's role (from DB); new add-ons show automatically */}
                    {Array.isArray(addons) && addons.map((addon) => (
                      <Grid item xs={12} sm={6} md={4} key={addon.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden'
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                            <Typography variant="overline" color="primary" fontWeight={600} sx={{ letterSpacing: 1 }}>
                              {addon.label}
                            </Typography>
                            <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                              ${addon.unit_price} <Typography component="span" variant="body2" color="text.secondary">/ unit</Typography>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flexGrow: 1 }}>
                              {addon.description || ''}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<ShoppingCart />}
                              onClick={() => addToCart(addon, 1)}
                              fullWidth
                              disabled={addonPurchaseBlocked}
                              sx={{ mt: 2, minHeight: 44 }}
                            >
                              Add to Cart
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Box>
            )}
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', px: 2 }}>
              Payments are processed securely by Stripe, an internationally recognized payment provider (PCI DSS compliant). We do not store your credit card or payment details.
            </Typography>
          </Box>
        );

      case 'teachers':
        return (
          <Paper elevation={2} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              My Teacher and Family
            </Typography>
            <Tabs
              value={relationshipTab}
              onChange={(_, value) => setRelationshipTab(value)}
              sx={{ mb: 3 }}
            >
              <Tab label="My Teacher" value="teachers" />
              <Tab label="Family" value="family" />
            </Tabs>
            {relationshipTab === 'teachers' && (
              <>
                {loadingTeachers ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </Box>
                ) : teachers.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Teacher Name</TableCell>
                          <TableCell>Subject</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Invitation</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teachers.map((teacher) => (
                          <TableRow key={teacher.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {teacher.teacher_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {teacher.teacher_email}
                              </Typography>
                            </TableCell>
                            <TableCell>{teacher.subject}</TableCell>
                            <TableCell>
                              {teacher.is_accepted === 'awaiting' ? (
                                <Typography variant="body2" color="warning.main" fontWeight="medium">
                                  Inactive
                                </Typography>
                              ) : teacher.is_accepted === 'accepted' ? (
                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                  Active
                                </Typography>
                              ) : teacher.is_accepted === 'exited' ? (
                                <Typography variant="body2" color="text.secondary">
                                  Inactive
                                </Typography>
                              ) : teacher.is_accepted === 'rejected' ? (
                                <Typography variant="body2" color="text.secondary">
                                  Rejected
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Unknown
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {teacher.is_accepted === 'awaiting' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleAcceptInvitation(teacher.invitation.id, 'accept')}
                                    sx={{ minWidth: 80 }}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleAcceptInvitation(teacher.invitation.id, 'reject')}
                                    sx={{ minWidth: 80 }}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              )}
                              {teacher.is_accepted === 'accepted' && (
                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                  Accepted
                                </Typography>
                              )}
                              {teacher.is_accepted === 'exited' && (
                                <Typography variant="body2" color="text.secondary">
                                  Exited
                                </Typography>
                              )}
                              {teacher.is_accepted === 'rejected' && (
                                <Typography variant="body2" color="text.secondary">
                                  Rejected
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {teacher.is_active && teacher.is_accepted === 'accepted' ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<ExitToApp />}
                                  onClick={() => {
                                    setSelectedRelationship(teacher);
                                    setExitDialog(true);
                                  }}
                                >
                                  Exit
                                </Button>
                              ) : teacher.is_accepted === 'awaiting' ? (
                                <Typography variant="body2" color="warning.main" fontWeight="medium">
                                  Accept to Activate
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No Action
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="text.secondary">
                      No teachers found. You will see your teachers here once they add you to their class.
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {relationshipTab === 'family' && (
              <>
                {loadingFamilyMembers ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </Box>
                ) : familyMembers.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Relation</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {familyMembers.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {member.name}
                                {member.is_current_user ? ' (You)' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {member.email}
                              </Typography>
                            </TableCell>
                            <TableCell>{member.member_role_label}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color={member.status === 'Active' ? 'success.main' : 'warning.main'}
                                fontWeight="medium"
                              >
                                {member.status}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="text.secondary">
                      No family members found for this account.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Determine which layout to use based on user role
  const isAdmin = user?.isAdmin || user?.is_admin;
  const isStudent = user?.isStudent || user?.is_student || (user?.role_name && user?.role_name.toString().toLowerCase() === 'student');
  const LayoutComponent = isAdmin ? AdminLayout : (isStudent ? StudentLayout : TeacherLayout);

  return (
    <LayoutComponent>
      <Box sx={{ p: 3 }}>
        {/* Title Section */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        </Box>

        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left Menu - Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, width: 280, flexShrink: 0 }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Profile Menu
              </Typography>
              <List>
                {menuItems.map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton
                      selected={activeMenu === item.id}
                      onClick={() => setActiveMenu(item.id)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1 }}>
            {renderMenuContent()}
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Profile Menu
          </Typography>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeMenu === item.id}
                  onClick={() => {
                    setActiveMenu(item.id);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Payment Dialog */}
      <Dialog 
        open={paymentDialog && !!selectedPlan} 
        onClose={handlePaymentCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedPlan && requiresPayment(selectedPlan.code) 
            ? `Upgrade to ${getPlanLabel(selectedPlan, userRole || 'Student')}` 
            : `Switch to ${getPlanLabel(selectedPlan, userRole || 'Student') || 'Free Plan'}`
          }
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Plan Details
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Plan:</strong> {getPlanLabel(selectedPlan, userRole || 'Student')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Price:</strong> {selectedPlan.price}{selectedPlan.period}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Duration:</strong> {selectedPlan.duration}
              </Typography>
              
              {requiresPayment(selectedPlan.code) ? (
                upgradePreviewLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading upgrade summary…</Typography>
                  </Box>
                ) : upgradePreview && upgradePreview.summary ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography component="span" display="block" sx={{ fontWeight: 600 }}>
                      {upgradePreview.current_plan_name} → {upgradePreview.new_plan_name}
                    </Typography>
                    <Typography component="span" display="block" sx={{ mt: 1 }}>
                      {upgradePreview.summary}
                    </Typography>
                    {upgradePreview.next_billing_date && (
                      <Typography component="span" display="block" sx={{ mt: 0.5 }} variant="body2">
                        Next billing date: {upgradePreview.next_billing_date}. You will then pay {upgradePreview.currency?.toUpperCase() || 'AUD'} {upgradePreview.next_recurring_formatted} per period.
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Your plan will upgrade immediately. You will be charged a prorated amount today for the remainder of the current period. From the next billing date you will be charged the full plan amount.
                    {(subscription?.effective_end_date || subscription?.end_date) && !isPlanExpired(subscription) && (
                      <Typography component="span" display="block" sx={{ mt: 1 }}>
                        Next billing date: {formatDate(subscription.effective_end_date || subscription.end_date)} · Next payment: {selectedPlan.currency || 'AUD'}{selectedPlan.price ?? '0'}/month
                      </Typography>
                    )}
                  </Alert>
                )
              ) : (
                <>
                  <Alert severity="success" sx={{ mt: 2 }}>
                    This is a free plan. No payment required.
                  </Alert>
                  {(subscription?.effective_end_date || subscription?.end_date) && subscription?.pricing_plan && !isPlanExpired(subscription) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Your current plan and features will continue until {formatDate(subscription.effective_end_date || subscription.end_date)}. The switch to free will take effect then. No charge today.
                    </Typography>
                  )}
                </>
              )}
              
              {requiresPayment(selectedPlan.code) && (
                <TextField
                  label="Promo code (optional)"
                  fullWidth
                  sx={{ mt: 2 }}
                  placeholder="Enter promo code"
                  value={paymentPromoCode}
                  onChange={(e) => setPaymentPromoCode(e.target.value)}
                />
              )}
              <TextField
                label="Reason for Change (Optional)"
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 2 }}
                placeholder="e.g., Need more features, Budget change, etc."
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentCancel} disabled={isProcessingPayment}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (selectedPlan) {
                if (requiresPayment(selectedPlan.code)) {
                  setIsProcessingPayment(true);
                  let shouldFallbackToCheckout = false;
                  try {
                    const upgradeResult = await startUpgrade(
                      selectedPlan.code,
                      changeReason || 'User initiated upgrade',
                      paymentPromoCode || undefined
                    );
                    if (upgradeResult?.applied) {
                      setPaymentDialog(false);
                      setSelectedPlan(null);
                      setUpgradePreview(null);
                      setChangeReason('');
                      setPaymentPromoCode('');
                      setSnackbar({
                        open: true,
                        message: upgradeResult?.message || 'Your plan has been upgraded.',
                        severity: 'success',
                        autoHideDuration: 6000
                      });
                      setActiveMenu('subscription');
                      loadUserData();
                      loadBillingSummary();
                      setIsProcessingPayment(false);
                      return;
                    }
                    if (upgradeResult?.requires_action && upgradeResult?.client_secret) {
                      setPaymentDialog(false);
                      setSelectedPlan(null);
                      setUpgradePreview(null);
                      setChangeReason('');
                      setPaymentPromoCode('');
                      navigate('/payment', {
                        replace: true,
                        state: {
                          isUpgradePayment: true,
                          clientSecret: upgradeResult.client_secret,
                          planName: selectedPlan.name,
                          fromProfile: true
                        }
                      });
                      setIsProcessingPayment(false);
                      return;
                    }
                  } catch (err) {
                    const upgradeErr = err.response?.data?.error;
                    const upgradeErrText = (upgradeErr || '').toLowerCase();
                    shouldFallbackToCheckout =
                      upgradeErrText.includes('no active stripe subscription found') ||
                      upgradeErrText.includes('regular checkout');

                    if (upgradeErr && !shouldFallbackToCheckout) {
                      setSnackbar({ open: true, message: `Upgrade could not be started: ${upgradeErr}`, severity: 'warning', autoHideDuration: 5000 });
                      setIsProcessingPayment(false);
                      return;
                    }
                  }
                  if (!shouldFallbackToCheckout) {
                    setIsProcessingPayment(false);
                    return;
                  }
                  try {
                    const data = await createCheckoutSubscription(
                      selectedPlan.code,
                      '/profile',
                      '/profile',
                      paymentPromoCode || undefined
                    );
                    if (data?.url) {
                      setPaymentDialog(false);
                      setSelectedPlan(null);
                      setChangeReason('');
                      setPaymentPromoCode('');
                      window.location.href = data.url;
                      return;
                    }
                  } catch (err) {
                    const msg = err.response?.data?.error || 'Could not start checkout.';
                    setSnackbar({ open: true, message: msg, severity: 'error', autoHideDuration: 6000 });
                    setIsProcessingPayment(false);
                    return;
                  }
                  navigate('/payment', {
                    replace: true,
                    state: {
                      planCode: selectedPlan.code,
                      planTitle: selectedPlan.name,
                      planPrice: selectedPlan.price,
                      planPeriod: selectedPlan.period,
                      planDuration: selectedPlan.duration,
                      changeReason: changeReason,
                      fromProfile: true
                    }
                  });
                  setIsProcessingPayment(false);
                } else {
                  // Free plan - update directly
                  setIsProcessingPayment(true);
                  try {
                    const response = await api.post(SUBSCRIPTION_CHANGE, {
                      plan_code: selectedPlan.code,
                      change_reason: changeReason || 'User switched to free plan from profile'
                    });
                    
                    if (response.status === 200) {
                      setPaymentDialog(false);
                      setSelectedPlan(null);
                      setChangeReason('');
                      setPaymentPromoCode('');
                      setSnackbar({
                        open: true,
                        message: response.data?.message || 'Subscription updated successfully!',
                        severity: 'success',
                        autoHideDuration: 6000
                      });
                      loadUserData();
                    } else {
                      handlePaymentError('Failed to update subscription');
                    }
                  } catch (error) {
                    console.error('Error updating subscription:', error);
                    const backendMessage = error.response?.data?.error;
                    handlePaymentError(
                      backendMessage || 'Failed to update subscription'
                    );
                  } finally {
                    setIsProcessingPayment(false);
                  }
                }
              }
            }}
            variant="contained"
            disabled={isProcessingPayment}
            startIcon={isProcessingPayment ? <CircularProgress size={20} /> : <Payment />}
          >
            {isProcessingPayment ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog
        open={downgradeDialogOpen}
        onClose={() => {
          setDowngradeDialogOpen(false);
          setPendingDowngrade(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {pendingDowngrade
            ? `Downgrade to ${pendingDowngrade.plan.name}`
            : 'Downgrade'}
        </DialogTitle>
        <DialogContent>
          {pendingDowngrade && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                {pendingDowngrade.currentSubscription &&
                pendingDowngrade.currentSubscription.end_date
                  ? `Your plan will downgrade to ${
                      pendingDowngrade.plan.name
                    } on ${formatDate(
                      pendingDowngrade.currentSubscription.end_date
                    )}.`
                  : `Your plan will downgrade to ${pendingDowngrade.plan.name} immediately.`}
              </Typography>
              {pendingDowngrade.currentSubscription &&
                pendingDowngrade.currentSubscription.end_date && (
                  <Typography variant="body1" gutterBottom>
                    You will continue to have{' '}
                    {plans.find(
                      (p) =>
                        p.id === pendingDowngrade.currentSubscription.pricing_plan
                    )?.name || 'your current plan'}{' '}
                    features until then.
                  </Typography>
                )}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You will not be charged anything today.
              </Typography>
              {pendingDowngrade.currentSubscription?.end_date && (
                <Typography variant="body2" color="text.secondary">
                  From {formatDate(pendingDowngrade.currentSubscription.end_date)} your payment will be {pendingDowngrade.plan.currency || 'AUD'}{pendingDowngrade.plan.price ?? '0'}/mo (lower than your current plan).
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDowngradeDialogOpen(false);
              setPendingDowngrade(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeDowngrade}
            variant="contained"
            color="primary"
          >
            Confirm downgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Add-on Dialog */}
      <Dialog
        open={purchaseDialog}
        onClose={() => setPurchaseDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Purchase {selectedAddon?.label}
        </DialogTitle>
        <DialogContent>
          {selectedAddon && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Add-on Details
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {selectedAddon.label}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Price:</strong> ${selectedAddon.unit_price} per unit
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Description:</strong> {selectedAddon.description}
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Quantity</InputLabel>
                <Select
                  value={purchaseQuantity}
                  label="Quantity"
                  onChange={(e) => setPurchaseQuantity(e.target.value)}
                >
                  {[1, 2, 3, 4, 5, 10, 15, 20].map((qty) => (
                    <MenuItem key={qty} value={qty}>
                      {qty}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                Total: ${(selectedAddon.unit_price * purchaseQuantity).toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialog(false)} disabled={processingPurchase}>
            Cancel
          </Button>
          <Button
            onClick={handlePurchaseAddon}
            variant="contained"
            disabled={processingPurchase}
            startIcon={processingPurchase ? <CircularProgress size={20} /> : <ShoppingCart />}
          >
            {processingPurchase ? 'Processing...' : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Teacher Dialog */}
      <Dialog
        open={exitDialog}
        onClose={() => setExitDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Exit Teacher Relationship</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to exit from {selectedRelationship?.teacher_name || 'this teacher'}? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedRelationship) {
                handleExitTeacher(selectedRelationship.id);
                setExitDialog(false);
                setSelectedRelationship(null);
              }
            }}
            variant="contained"
            color="error"
          >
            Exit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cart Dialog */}
      <Dialog
        open={cartDialog}
        onClose={() => setCartDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Shopping Cart ({cart.length} items)
        </DialogTitle>
        <DialogContent>
          {loadingCart ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : cart.length > 0 ? (
            <Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.map((item) => (
                      <TableRow key={item.addon_id}>
                        <TableCell>{item.addon_label}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>${item.unit_price}</TableCell>
                        <TableCell>${item.total_price}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeFromCart(item.addon_id)}
                          >
                            <HighlightOff />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TextField
                label="Promo code (optional)"
                fullWidth
                sx={{ mt: 2 }}
                placeholder="Enter promo code"
                value={addonPromoCode}
                onChange={(e) => setAddonPromoCode(e.target.value)}
              />
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography variant="h6">
                  Total: ${cart.reduce((sum, item) => sum + item.total_price, 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Your cart is empty
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartDialog(false)}>
            Continue Shopping
          </Button>
          {cart.length > 0 && (
            <Button
              onClick={handleCheckout}
              variant="contained"
              disabled={processingCheckout}
              startIcon={processingCheckout ? <CircularProgress size={20} /> : <Payment />}
            >
              {processingCheckout ? 'Processing...' : 'Proceed to Checkout'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LayoutComponent>
  );
};

export default Profile;
